import React, { useCallback } from 'react';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilm } from '@fortawesome/pro-light-svg-icons/faFilm';
import { faImage } from '@fortawesome/pro-light-svg-icons/faImage';
import { TypedMemo } from '../../../../common/types';
import { Panel } from '../../../components/primitives/Panel';
import { PostMediaKind } from '../../platformMeta';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the picker; each part uses its own slice of it */
import styles from './ComposerMediaPicker.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PickerTileProps {
  tileKey: string;
  name: string;
  /** "9:16 · Video · 0:30". */
  facts: string;
  kind: PostMediaKind;
  /** The absolute url of the picture, or null. */
  pictureUrl: string | null;
  /** "r2"; absent for a file with no revision (the handover). */
  badge?: string;
  isSelected: boolean;
  onSelect: (key: string) => void;
  /** A double click attaches the file at once. */
  onAttach: (key: string) => void;
}

/** One file of the picker: the picture, the revision, the name and the facts. A click selects it. */
const PickerTileComponent: React.FC<PickerTileProps> = ({
  tileKey,
  name,
  facts,
  kind,
  pictureUrl,
  badge,
  isSelected,
  onSelect,
  onAttach,
}) => {
  const select = useCallback(() => onSelect(tileKey), [onSelect, tileKey]);
  const attach = useCallback(() => onAttach(tileKey), [onAttach, tileKey]);

  return (
    <Panel nested as="li" className={clsx(styles.fileTile, isSelected && styles.fileTileOn)}>
      <button
        type="button"
        className={styles.tileButton}
        aria-pressed={isSelected}
        data-testid={`picker-tile-${tileKey}`}
        onClick={select}
        onDoubleClick={attach}
      >
        <span className={styles.tileFrame}>
          {pictureUrl === null ? (
            <FontAwesomeIcon icon={kind === 'image' ? faImage : faFilm} size="2x" />
          ) : (
            <img className={styles.tileImage} src={pictureUrl} alt="" loading="lazy" />
          )}
          {badge !== undefined && <span className={styles.tileRevision}>{badge}</span>}
        </span>
        <span className={styles.tileName} title={name}>
          {name}
        </span>
        <span className={styles.tileFacts}>{facts}</span>
      </button>
    </Panel>
  );
};

export const PickerTile = TypedMemo(PickerTileComponent);
